<template>
  <div
    class="d-flex align-center justify-center align-self-center flex-row gap-1 w-100 comet-i18n-exclude">
    <template v-for="(icon, index) in assetVisitTypeIcon" :key="index">
      <v-icon>{{ icon }}</v-icon>
    </template>
    <div class="typography-body-sm-bold gap-4">{{ assetVisitTypeLabel }}</div>
  </div>

  <div class="d-flex flex-column gap-6" v-if="shouldShowAssetDetailsForm">
    <div class="d-flex flex-column gap-6" v-for="(_, i) of assetContainerData" :key="i">
      <div class="custom-field comet-i18n-exclude">
        <v-label class="custom-field__label required">{{ t('assetDetails.assetType') }}</v-label>
        <v-select
          variant="outlined"
          color="primary"
          :placeholder="t('assetDetails.assetTypePlaceholder')"
          :id="`asset-visit-form-container-type-${i}`"
          v-model="assetContainerData[i].containerType"
          :rules="[
            requiredValidator(assetContainerData[i].containerType, t('assetDetails.trailerType'))
          ]"
          :data-testid="`asset-visit-form-container-type-${i}`"
          :items="assetContainerTypes"
          item-title="title"
          item-value="value"
          class="flex-grow-1"
          hide-details></v-select>
      </div>

      <div class="custom-field comet-i18n-exclude">
        <v-label class="custom-field__label required">{{ t('assetDetails.assetId') }}</v-label>
        <v-text-field
          color="primary"
          variant="outlined"
          :placeholder="t('assetDetails.assetIdPlaceholder')"
          :data-testid="`asset-visit-form-container-code-${i}`"
          v-model="assetContainerData[i].containerCode"
          maxlength="50"
          required
          :rules="[
            requiredValidator(assetContainerData[i].containerCode, t('assetDetails.trailerId'))
          ]"
          class="flex-grow-1"
          hide-details></v-text-field>
      </div>
      <v-btn
        v-if="assetContainerData.length > 1 && i > 0"
        variant="text"
        color="error"
        class="comet-i18n-exclude"
        @click="handleRemove(i)"
        style="align-self: flex-end">
        {{ t('assetDetails.remove') }}
      </v-btn>
    </div>

    <v-btn
      block
      variant="outlined"
      class="comet-i18n-exclude"
      v-if="assetContainerData.length < maxContainerData"
      @click="handleAdd"
      prepend-icon="mdi-plus">
      {{ t('assetDetails.addAnother') }}
    </v-btn>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { AssetContainerType } from '@nova/core';

export default defineComponent({
  name: 'AssetDetailsForm',
  props: {
    shouldShowAssetDetailsForm: {
      type: Boolean,
      required: true
    },
    assetVisitType: {
      type: String,
      required: true
    },
    assetVisitTypes: {
      type: Array as () => Array<{ value: string; label: string; icons?: string[] }>,
      required: true
    },
    assetContainerData: {
      type: Array as () => Array<{ containerCode: string; containerType: AssetContainerType }>,
      required: true
    },
    assetContainerTypes: {
      type: Array as () => Array<{ title: string; value: AssetContainerType }>,
      required: true
    },
    maxContainerData: {
      type: Number,
      required: true
    }
  },
  emits: ['update:assetContainerData'],
  setup(props, { emit }) {
    const { t } = useI18n();

    const assetVisitTypeLabel = computed(
      () => props.assetVisitTypes.find(v => v.value === props.assetVisitType)?.label
    );

    const assetVisitTypeIcon = computed(
      () => props.assetVisitTypes.find(v => v.value === props.assetVisitType)?.icons ?? []
    );

    const requiredValidator = (value: string, label: string) =>
      !value || value?.length === 0
        ? t('common.validation.fieldRequiredWithLabel', { label })
        : true;

    const handleRemove = (index: number) => {
      const newData = [...props.assetContainerData];
      newData.splice(index, 1);
      emit('update:assetContainerData', newData);
    };

    const handleAdd = () => {
      const newData = [
        ...props.assetContainerData,
        {
          containerCode: '',
          containerType: AssetContainerType.Trailer
        }
      ];

      emit('update:assetContainerData', newData);

      nextTick(() => {
        const lastContainerType = document.getElementById(
          `asset-visit-form-container-type-${newData.length - 1}`
        );

        if (lastContainerType) {
          lastContainerType.focus();
          lastContainerType.scrollIntoView({ behavior: 'smooth' });
        }
      });
    };

    return {
      t,
      assetVisitTypeLabel,
      assetVisitTypeIcon,
      requiredValidator,
      handleRemove,
      handleAdd
    };
  }
});
</script>
